import React, {FC, useMemo} from 'react';
import {useInformerConfigState} from '@/informers/services/informerConfigReducer';
import {useLocalizer} from '@/common/services/localizer';
import {ClockType, TemperatureUnit, PressureUnit, SpeedUnit} from '@/common/constants/units';
import {languageNames} from '@/common/constants/language';
import {FieldSelect} from '@/common/components/FieldSelect';
import {Checkbox} from '@/common/components/Checkbox';
import styles from './FormContent.css';

export const supportedLanguages = ['uk', 'ru', 'en', 'de', 'pl'];
const getLanguageName = (option: string) => languageNames[option];

const contentItemFieldNames = ['showWindSpeed', 'showHumidity', 'showPressure', 'showPrecipitation'] as const;

const timeFormatOptions = Object.values(ClockType);
const temperatureOptions = Object.values(TemperatureUnit);
const pressureOptions = Object.values(PressureUnit);
const windSpeedOptions = Object.values(SpeedUnit);

interface FormContentProps {
	onNextStage(): void;
}

export const FormContent: FC<FormContentProps> = props => {
	const {onNextStage} = props;

	const t = useLocalizer();

	const {state, dispatch} = useInformerConfigState();

	const currentDate = useMemo(() => new Date(), []);

	const handleComplete = (event: React.FormEvent) => {
		event.preventDefault();
		onNextStage();
	};
	const handleLanguageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
		const value = event.currentTarget.value;
		dispatch({type: 'setValues', values: {language: value}});
	};

	const handleTimeFormatChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
		const value = event.currentTarget.value as ClockType;
		dispatch({type: 'setValues', values: {timeFormat: value}});
	};

	const handleContentItemShowSwitch = (event: React.ChangeEvent<HTMLInputElement>) => {
		const name = event.currentTarget.name as keyof typeof contentItemFieldNames;
		const value = event.currentTarget.checked;
		dispatch({type: 'setValues', values: {[name]: value}});
	};

	const handleTemperatureUnitChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
		const value = event.currentTarget.value as TemperatureUnit;
		dispatch({type: 'setValues', values: {temperatureUnit: value}});
	};

	const handlePressureUnitChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
		const value = event.currentTarget.value as PressureUnit;
		dispatch({type: 'setValues', values: {pressureUnit: value}});
	};

	const handleWindSpeedUnitChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
		const value = event.currentTarget.value as SpeedUnit;
		dispatch({type: 'setValues', values: {windSpeedUnit: value}});
	};

	const getTimeFormatName = (option: ClockType) => {
		return currentDate
			.toLocaleTimeString('en-gb', {
				hour: 'numeric',
				minute: '2-digit',
				hour12: option === ClockType['12h'],
			})
			.toUpperCase();
	};
	const getTemperatureUnitName = (option: string) => t(`temperatureUnitOptions.${option}`);
	const getPressureUnitName = (option: string) => t(`pressureUnitOptions.${option}`);
	const getWindSpeedUnitName = (option: string) => t(`speedUnitOptions.${option}`);
	const getContentItemName = (option: string) => t(`contentEntries.${option}`);

	const maxIndicatorsAllowed = 3;
	const indicatorsLimitReached =
		contentItemFieldNames.reduce((count, fieldName) => (state[fieldName] ? count + 1 : count), 0) >=
		maxIndicatorsAllowed;

	return (
		<form onSubmit={handleComplete}>
			<p className={styles.labelText}>{t('languageTitle')}</p>
			<FieldSelect
				options={supportedLanguages}
				value={state.language}
				getDisplayValue={getLanguageName}
				onChange={handleLanguageChange}
			/>

			<p className={styles.labelText}>{t('timeFormatTitle')}</p>
			<FieldSelect
				options={timeFormatOptions}
				value={state.timeFormat}
				getDisplayValue={getTimeFormatName}
				onChange={handleTimeFormatChange}
			/>

			<p className={styles.labelText}>{t('contentMarksTitle')}</p>
			{contentItemFieldNames.map(field => (
				<label key={field} className={styles.checkboxRow}>
					<Checkbox
						className={styles.checkboxInput}
						name={field}
						checked={state[field]}
						disabled={!state[field] && indicatorsLimitReached}
						onChange={handleContentItemShowSwitch}
					/>
					<span className={styles.checkboxLabel}>{getContentItemName(field)}</span>
				</label>
			))}

			<p className={styles.labelText}>{t('unitsTitle')}</p>
			<FieldSelect
				className={styles.unitSelectField}
				options={temperatureOptions}
				value={state.temperatureUnit}
				getDisplayValue={getTemperatureUnitName}
				onChange={handleTemperatureUnitChange}
			/>
			<FieldSelect
				className={styles.unitSelectField}
				options={pressureOptions}
				value={state.pressureUnit}
				getDisplayValue={getPressureUnitName}
				onChange={handlePressureUnitChange}
			/>
			<FieldSelect
				className={styles.unitSelectField}
				options={windSpeedOptions}
				value={state.windSpeedUnit}
				getDisplayValue={getWindSpeedUnitName}
				onChange={handleWindSpeedUnitChange}
			/>
		</form>
	);
};

FormContent.displayName = 'FormContent';
